import React from "react";
import {
  Paper, Stack, Typography, Box, Skeleton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from "@mui/material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { ResponsivePie } from "@nivo/pie";

import InfoButton from "../Clinical/InfoButton";
import { getBHStatusColors } from "../../constants/bhDashboard";
import { HEALTH_STATUS_COLORS } from "../../constants/clinicalDashboard";

const GENERAL_STATUS_COLS = [
  { key: "well", label: "Well" },
  { key: "acute", label: "Acute" },
  { key: "chronic", label: "Chronic" },
  { key: "atRisk", label: "At Risk" },
  { key: "complex", label: "Complex" },
  { key: "endOfLife", label: "End of Life" },
];

/**
 * Panel 2: BH Risk Stratification — Donut chart + Cross-Tab table.
 */
export default function PanelBHRisk({ data, loading, helpContent }) {
  if (loading) {
    return (
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
        <Skeleton variant="rectangular" animation="wave" height={350} sx={{ borderRadius: 2 }} />
      </Paper>
    );
  }

  const distribution = data?.distribution || [];
  const crossTab = data?.crossTab || [];

  // Donut data (nivo format)
  const pieData = distribution.map((d) => ({
    id: d.status,
    label: d.status,
    value: d.count,
    color: d.color,
  }));

  return (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
      <Stack spacing={2.5}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 28, height: 28, borderRadius: 1.5,
              bgcolor: "#F3E8FF", color: "#7C3AED",
            }}
          >
            <AssessmentIcon sx={{ fontSize: 16 }} />
          </Box>
          <Box>
            <Typography variant="subtitle2" fontWeight={700}>BH Risk Stratification</Typography>
            <Typography variant="caption" color="text.secondary">Distribution by behavioral health severity level</Typography>
          </Box>
          <InfoButton helpContent={helpContent} />
        </Stack>

        {/* Content: Donut + Legend side by side */}
        <Box sx={{ display: "grid", gap: 3, gridTemplateColumns: { xs: "1fr", md: "1fr 1.2fr" }, alignItems: "start" }}>
          {/* Donut Chart */}
          <Box sx={{ height: 280, position: "relative" }}>
            {pieData.length > 0 ? (
              <ResponsivePie
                data={pieData}
                colors={{ datum: "data.color" }}
                margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                innerRadius={0.55}
                padAngle={1}
                cornerRadius={3}
                activeOuterRadiusOffset={6}
                enableArcLabels={false}
                enableArcLinkLabels={false}
                tooltip={({ datum }) => (
                  <Paper sx={{ px: 1.5, py: 0.8, fontSize: "0.75rem" }}>
                    <strong>{datum.label}</strong>: {datum.value} ({((datum.value / distribution.reduce((a, b) => a + b.count, 0)) * 100).toFixed(1)}%)
                  </Paper>
                )}
              />
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", pt: 10 }}>No data</Typography>
            )}
          </Box>

          {/* Distribution Legend */}
          <Stack spacing={0.8}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
              Severity Distribution
            </Typography>
            {distribution.map((d) => {
              const total = distribution.reduce((a, b) => a + b.count, 0);
              return (
                <Stack key={d.status} direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: d.color, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ flex: 1, fontSize: "0.78rem" }}>{d.status}</Typography>
                  <Typography variant="body2" fontWeight={700} sx={{ fontSize: "0.78rem", minWidth: 36, textAlign: "right" }}>
                    {d.count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.72rem", minWidth: 40, textAlign: "right" }}>
                    {total > 0 ? `${((d.count / total) * 100).toFixed(1)}%` : "0%"}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        </Box>

        {/* Cross-Tab Table */}
        {crossTab.length > 0 && (
          <Box>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5, mb: 1, display: "block" }}>
              BH Status x General Health Status
            </Typography>
            <TableContainer sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "action.hover" }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.72rem" }}>BH Status</TableCell>
                    {GENERAL_STATUS_COLS.map((col) => (
                      <TableCell key={col.key} align="center" sx={{ fontWeight: 700, fontSize: "0.72rem" }}>
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {crossTab.map((row) => {
                    const bhColors = getBHStatusColors(row.bhStatus);
                    return (
                      <TableRow key={row.bhStatus} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                        <TableCell sx={{ fontSize: "0.72rem" }}>
                          <Box
                            component="span"
                            sx={{
                              px: 0.8, py: 0.3, borderRadius: 1,
                              bgcolor: bhColors.bg, color: bhColors.text,
                              fontWeight: 600, fontSize: "0.68rem",
                            }}
                          >
                            {row.bhStatus}
                          </Box>
                        </TableCell>
                        {GENERAL_STATUS_COLS.map((col) => {
                          const val = row[col.key] || 0;
                          const genColors = HEALTH_STATUS_COLORS[col.label] || HEALTH_STATUS_COLORS["No Status"];
                          return (
                            <TableCell key={col.key} align="center" sx={{ fontSize: "0.72rem" }}>
                              {val > 0 ? (
                                <Box
                                  component="span"
                                  sx={{
                                    px: 0.6, py: 0.2, borderRadius: 1,
                                    bgcolor: genColors.bg, color: genColors.text,
                                    fontWeight: 700, fontSize: "0.7rem",
                                  }}
                                >
                                  {val}
                                </Box>
                              ) : (
                                <Typography variant="caption" color="text.disabled">-</Typography>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}
